import { useNavigate } from 'react-router-dom';
import {
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_TEL,
  LEGAL_OPERATOR_NAME,
  LEGAL_ADDRESS_STREET,
  LEGAL_ADDRESS_CITY,
  LEGAL_ADDRESS_COUNTRY,
} from '../constants/contact';

export default function AGB() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            Zurück
          </button>
          <span className="text-gray-300">|</span>
          <span className="text-sm font-semibold text-gray-700" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Candlear Booking
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">

          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Allgemeine Geschäftsbedingungen
          </h1>
          <p className="text-sm text-gray-400 mb-8">Stand: Oktober 2026</p>

          {/* Anbieter */}
          <section className="mb-8 p-5 bg-gray-50 rounded-xl border border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Anbieter</p>
            <p className="text-sm text-gray-800 leading-relaxed">
              <strong>{LEGAL_OPERATOR_NAME}</strong><br />
              (nachfolgend „Synergic Systems" oder „Anbieter")<br />
              {LEGAL_ADDRESS_STREET}<br />
              {LEGAL_ADDRESS_CITY}, {LEGAL_ADDRESS_COUNTRY}<br />
              <br />
              E-Mail: <a href="mailto:support@candlear.de" className="text-[#FF6D00] hover:underline">support@candlear.de</a><br />
              Telefon:{' '}
              <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-[#FF6D00] hover:underline">
                {SUPPORT_PHONE_DISPLAY}
              </a>
              <br />
              Website: <a href="https://candlear.de" className="text-[#FF6D00] hover:underline">https://candlear.de</a>
            </p>
          </section>

          <AgbSection para="§ 1" title="Geltungsbereich und Vertragsparteien">
            <p>
              <strong>1.1</strong> Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Verträge über die Nutzung
              der Software-as-a-Service-Plattform <strong>Candlear Booking</strong> (nachfolgend „die Plattform" oder „der Dienst"),
              die zwischen dem Anbieter und dem jeweiligen Nutzer geschlossen werden.
            </p>
            <p>
              <strong>1.2</strong> Der Dienst richtet sich ausschließlich an <strong>Unternehmer</strong> im Sinne von § 14 BGB,
              d. h. natürliche oder juristische Personen oder rechtsfähige Personengesellschaften, die bei Abschluss des Vertrags
              in Ausübung ihrer gewerblichen oder selbständigen beruflichen Tätigkeit handeln (z. B. Friseursalons, Coaches,
              Berater, Handwerksbetriebe). Die Nutzung durch Verbraucher (§ 13 BGB) ist ausgeschlossen.
            </p>
            <p>
              <strong>1.3</strong> Abweichende, entgegenstehende oder ergänzende Allgemeine Geschäftsbedingungen des Nutzers
              werden nicht Vertragsbestandteil, es sei denn, der Anbieter stimmt ihrer Geltung ausdrücklich schriftlich zu.
            </p>
          </AgbSection>

          <AgbSection para="§ 2" title="Vertragsschluss">
            <p>
              <strong>2.1</strong> Der Nutzungsvertrag kommt mit Abschluss der Registrierung auf{' '}
              <a href="https://candlear.de" className="text-[#FF6D00] hover:underline">candlear.de</a> zustande. Voraussetzung ist,
              dass der Nutzer die erforderlichen Pflichtangaben macht, diese AGB und den Auftragsverarbeitungsvertrag (AVV)
              durch Bestätigung bei der Registrierung annimmt und die Datenschutzerklärung zur Kenntnis nimmt. Mit Abschluss
              der Registrierung erhält der Nutzer Zugang zum Admin-Dashboard.
            </p>
            <p>
              <strong>2.2</strong> Das kostenpflichtige Abonnement kommt zustande, wenn der Nutzer den Bestellvorgang im
              Zahlungsformular von Stripe (Checkout) abschließt. Vor Abschluss werden dort Preis, Abrechnungszeitraum und
              Testphase angezeigt.
            </p>
            <p>
              <strong>2.3</strong> Der Anbieter behält sich vor, Registrierungsanfragen ohne Angabe von Gründen abzulehnen,
              insbesondere wenn ein Verdacht auf missbräuchliche Nutzung besteht.
            </p>
          </AgbSection>

          <AgbSection para="§ 3" title="Leistungsumfang">
            <p className="mb-3">
              <strong>3.1</strong> Der Anbieter stellt dem Nutzer die Plattform <strong>Candlear Booking</strong> als
              cloudbasiertes Buchungssystem zur Verfügung. Der aktuelle Leistungsumfang umfasst insbesondere:
            </p>
            <ul className="list-disc list-inside space-y-1.5 text-gray-700 mb-4">
              <li><strong>Admin-Dashboard:</strong> webbasierte Oberfläche zur Verwaltung von Terminen, Ressourcen, Verfügbarkeiten und Einstellungen</li>
              <li><strong>Buchungs-Widget:</strong> einbettbares Chat-Widget für die Website des Nutzers, über das Endkunden Termine buchen können</li>
              <li><strong>KI-gestützte Buchungsassistenz:</strong> automatisierte Terminvermittlung via Chat (Google Gemini)</li>
              <li><strong>E-Mail-Benachrichtigungen:</strong> Buchungsbestätigungen und Bestätigungscodes an Endkunden</li>
              <li><strong>API-Zugang:</strong> Buchungsfunktionen über einen API-Key für eigene Integrationen</li>
              <li><strong>Datenschutz-Tools:</strong> Datenexport und Account-Löschung im Dashboard</li>
            </ul>
            <p>
              <strong>3.2</strong> Der Anbieter erbringt die Leistungen auf Basis des zum Zeitpunkt der Nutzung aktuellen
              Stands der Plattform. Ein Anspruch auf bestimmte Funktionen oder deren dauerhafte Beibehaltung besteht nicht,
              sofern der Kernleistungsumfang (Terminbuchung und Admin-Verwaltung) nicht wesentlich eingeschränkt wird.
              Änderungen werden nach § 11 dieser AGB kommuniziert.
            </p>
            <p>
              <strong>3.3</strong> Die Plattform stellt ausschließlich die technische Infrastruktur bereit. Der Nutzer ist
              selbst verantwortlich für die Korrektheit seiner Verfügbarkeiten, Ressourcen und sonstigen Einstellungen.
            </p>
            <p>
              <strong>3.4</strong> Für die Einbindung des Buchungs-Widgets auf der eigenen Website ist der Nutzer selbst
              technisch verantwortlich. Der Anbieter stellt den notwendigen Einbettungscode bereit.
            </p>
          </AgbSection>

          <AgbSection para="§ 4" title="Kostenlose Testphase">
            <p>
              <strong>4.1</strong> Neue Nutzer erhalten beim ersten Abschluss eines Abonnements (§ 2.2) eine{' '}
              <strong>kostenlose Testphase von 30 Tagen</strong>, in der der vollständige Leistungsumfang ohne Zahlung genutzt
              werden kann. Die Testphase beginnt mit Abschluss des Bestellvorgangs.
            </p>
            <p>
              <strong>4.2</strong> Für die Aktivierung der Testphase ist die Angabe einer gültigen Zahlungsmethode (Kreditkarte)
              über den Zahlungsdienstleister Stripe erforderlich. Während der Testphase erfolgt <strong>keine Belastung</strong>.
            </p>
            <p>
              <strong>4.3</strong> Wenn der Nutzer das Abonnement nicht vor Ablauf der 30 Tage kündigt, wird es automatisch
              in ein kostenpflichtiges Abonnement umgewandelt und die hinterlegte Zahlungsmethode belastet.
              Der Nutzer erhält vor Umwandlung eine E-Mail-Erinnerung an die von ihm angegebene E-Mail-Adresse.
            </p>
            <p>
              <strong>4.4</strong> Die Testphase kann pro Person und pro Unternehmen nur einmal in Anspruch genommen werden.
              Der Anbieter behält sich vor, mehrfache Testphasen für dasselbe Unternehmen oder dieselbe Person zu verweigern.
            </p>
          </AgbSection>

          <AgbSection para="§ 5" title="Preise und Zahlung">
            <p>
              <strong>5.1</strong> Nach Ablauf der Testphase ist die Nutzung der Plattform kostenpflichtig. Es gilt der Preis, der dem
              Nutzer im Bestellvorgang (§ 2.2) vor Abschluss des Abonnements angezeigt wird. Der angezeigte Preis ist
              ein <strong>Endpreis</strong>. Gemäß § 19 UStG (Kleinunternehmerregelung)
              wird keine Umsatzsteuer erhoben und ausgewiesen.
            </p>
            <p>
              <strong>5.2</strong> Die Abrechnung erfolgt <strong>monatlich im Voraus</strong>. Die Zahlung wird automatisch
              am jeweiligen Abrechnungstag über die hinterlegte Zahlungsmethode eingezogen.
            </p>
            <p>
              <strong>5.3</strong> Die Zahlungsabwicklung erfolgt über <strong>Stripe Payments Europe, Limited</strong> (Dublin, Irland). Es gelten die
              Nutzungsbedingungen von Stripe. Akzeptierte Zahlungsmittel sind Kreditkarten (Visa, Mastercard, American Express u. a.)
              sowie weitere von Stripe angebotene Zahlungsmethoden.
            </p>
            <p>
              <strong>5.4</strong> Schlägt eine Zahlung fehl, wird der Nutzer per E-Mail informiert. Der Anbieter ist berechtigt,
              den Zugang zur Plattform vorübergehend zu sperren, wenn eine fällige Zahlung trotz Mahnung nicht innerhalb von
              <strong> 14 Tagen</strong> nach Fälligkeit eingeht. Der Zugang wird nach vollständigem Zahlungseingang unverzüglich
              wiederhergestellt.
            </p>
            <p>
              <strong>5.5</strong> Rechnungen werden elektronisch per E-Mail oder über das Admin-Dashboard bereitgestellt.
              Rechnungen sind nach den gesetzlichen Vorschriften (§ 147 Abs. 3 AO, § 257 Abs. 4 HGB, § 14b UStG) 8 Jahre aufzubewahren.
            </p>
            <p>
              <strong>5.6</strong> Preisänderungen teilt der Anbieter dem Nutzer mit einer Frist von mindestens <strong>30 Tagen</strong> vor
              Inkrafttreten per E-Mail mit. Der Nutzer kann das Abonnement in diesem Fall bis zum Inkrafttreten der
              Preisänderung kostenfrei zu diesem Zeitpunkt kündigen (Sonderkündigungsrecht); bis dahin gilt der bisherige
              Preis. Auf das Sonderkündigungsrecht weist der Anbieter in der Mitteilung ausdrücklich hin. Kündigt der Nutzer
              nicht, gilt ab Inkrafttreten der neue Preis.
            </p>
          </AgbSection>

          <AgbSection para="§ 6" title="Laufzeit und Kündigung">
            <p>
              <strong>6.1</strong> Der Vertrag wird auf <strong>unbestimmte Zeit</strong> geschlossen. Nach Ablauf der
              Testphase verlängert sich das Abonnement automatisch um jeweils einen Monat, sofern es nicht fristgerecht
              gekündigt wird.
            </p>
            <p>
              <strong>6.2</strong> Der Nutzer kann das Abonnement <strong>jederzeit</strong> zum Ende des laufenden
              Abrechnungszeitraums kündigen. Die Kündigung ist möglich:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2 mb-3">
              <li>Im Admin-Dashboard unter <strong>Einstellungen → Abonnement → Kündigen</strong></li>
              <li>
                Per E-Mail an <a href="mailto:support@candlear.de" className="text-[#FF6D00] hover:underline">support@candlear.de</a>{' '}
                oder telefonisch unter{' '}
                <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-[#FF6D00] hover:underline">
                  {SUPPORT_PHONE_DISPLAY}
                </a>
              </li>
            </ul>
            <p>
              <strong>6.3</strong> Nach der Kündigung hat der Nutzer bis zum Ende des bereits bezahlten Abrechnungszeitraums
              vollen Zugang zur Plattform. Es erfolgt <strong>keine anteilige Erstattung</strong> für den verbleibenden Zeitraum.
            </p>
            <p>
              <strong>6.4</strong> Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt.
              Ein wichtiger Grund für eine außerordentliche Kündigung durch den Anbieter liegt insbesondere vor, wenn:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2 mb-3">
              <li>der Nutzer trotz Mahnung mit Zahlungen in Verzug bleibt</li>
              <li>der Nutzer wesentliche Pflichten aus diesen AGB verletzt</li>
              <li>der Nutzer die Plattform missbräuchlich oder rechtswidrig nutzt</li>
            </ul>
            <p>
              <strong>6.5</strong> Nach Beendigung des Vertrags – gleich aus welchem Grund – werden alle Daten des Nutzers
              und seiner Endkunden gemäß der <a href="/datenschutz" className="text-[#FF6D00] hover:underline">Datenschutzerklärung</a> und
              dem <a href="/avv" className="text-[#FF6D00] hover:underline">Auftragsverarbeitungsvertrag (AVV)</a> gelöscht.
              Gesetzliche Aufbewahrungspflichten (insb. für Rechnungen) bleiben davon unberührt.
            </p>
            <p>
              <strong>6.6</strong> Löscht der Nutzer seinen Account eigenständig über das Admin-Dashboard
              (<strong>Einstellungen → Datenschutz → Account löschen</strong>), wird das Abonnement sofort beendet und
              alle Daten werden unwiderruflich gelöscht. Eine Erstattung bereits gezahlter Beträge erfolgt in diesem Fall nicht.
            </p>
          </AgbSection>

          <AgbSection para="§ 7" title="Pflichten des Nutzers">
            <p className="mb-3">
              <strong>7.1 Zugangsdaten:</strong> Der Nutzer ist verpflichtet, seine Zugangsdaten vertraulich zu behandeln
              und vor dem Zugriff durch Dritte zu schützen. Bei Verdacht auf unbefugten Zugriff ist der Anbieter unverzüglich
              zu informieren.
            </p>
            <p className="mb-3">
              <strong>7.2 Richtigkeit der Angaben:</strong> Der Nutzer stellt sicher, dass seine bei der Registrierung
              angegebenen Daten vollständig und korrekt sind. Änderungen (insb. E-Mail-Adresse, Rechnungsadresse) sind
              unverzüglich im Dashboard zu aktualisieren.
            </p>
            <p className="mb-3">
              <strong>7.3 Datenschutzverantwortung:</strong> Der Nutzer ist als Verantwortlicher im Sinne der DSGVO für
              die Verarbeitung der Daten seiner Endkunden verantwortlich. Er verpflichtet sich:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2 mb-4">
              <li>seinen Endkunden gegenüber eine eigene, datenschutzkonforme Datenschutzerklärung bereitzustellen,
                die die Nutzung von Candlear Booking als Auftragsverarbeiter transparent macht</li>
              <li>nur Daten zu verarbeiten, für die eine gültige Rechtsgrundlage (Art. 6 DSGVO) besteht</li>
              <li>den abgeschlossenen AVV (Art. 28 DSGVO) einzuhalten</li>
            </ul>
            <p className="mb-3">
              <strong>7.4 Unzulässige Nutzung:</strong> Folgende Nutzungen sind ausdrücklich untersagt:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2 mb-4">
              <li>Weitergabe des Accounts oder der API-Keys an Dritte ohne vorherige schriftliche Zustimmung des Anbieters</li>
              <li>Nutzung der Plattform für rechtswidrige Zwecke oder zur Verarbeitung illegaler Inhalte</li>
              <li>Automatisierter Missbrauch der API (über die technischen Rate-Limits hinaus)</li>
              <li>Reverse Engineering, Dekompilierung oder sonstige Versuche, den Quellcode der Plattform zu extrahieren</li>
              <li>Weiterverkauf oder Sub-Lizenzierung der Plattform an Dritte (White-Labeling nur nach schriftlicher Vereinbarung)</li>
            </ul>
            <p>
              <strong>7.5 Nutzung durch Mitarbeiter:</strong> Der Nutzer haftet für die Handlungen seiner Mitarbeiter
              oder sonstigen Personen, die er Zugang zur Plattform gewährt, wie für eigene Handlungen.
            </p>
          </AgbSection>

          <AgbSection para="§ 8" title="Verfügbarkeit und Support">
            <p>
              <strong>8.1</strong> Der Anbieter strebt eine Verfügbarkeit der Plattform von <strong>99 % im Monatsmittel</strong> an.
              Hiervon ausgenommen sind geplante Wartungsarbeiten sowie Ausfälle, die auf Umständen außerhalb des
              Einflussbereichs des Anbieters beruhen (höhere Gewalt, Ausfälle bei Google Firebase o. ä. Drittanbieter-Infrastruktur).
            </p>
            <p>
              <strong>8.2</strong> Geplante Wartungsarbeiten werden dem Nutzer nach Möglichkeit im Voraus per E-Mail
              oder im Admin-Dashboard angekündigt und möglichst außerhalb der üblichen Geschäftszeiten (Mo–Fr, 9–18 Uhr)
              durchgeführt.
            </p>
            <p>
              <strong>8.3</strong> Support wird per E-Mail an{' '}
              <a href="mailto:support@candlear.de" className="text-[#FF6D00] hover:underline">support@candlear.de</a>{' '}
              und telefonisch unter{' '}
              <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-[#FF6D00] hover:underline">
                {SUPPORT_PHONE_DISPLAY}
              </a>{' '}
              angeboten. Der Anbieter bemüht sich, E-Mail-Anfragen innerhalb von <strong>2 Werktagen</strong> zu beantworten.
              Telefonische Erreichbarkeit ist nicht durchgängig garantiert; bei Nichterreichen kann eine Nachricht per E-Mail hinterlassen werden.
              Ein Anspruch auf garantierte Reaktionszeiten besteht – insbesondere im Rahmen des aktuellen Preismodells – nicht.
            </p>
            <p>
              <strong>8.4</strong> Die KI-Chat-Funktion (Google Gemini) unterliegt den Nutzungsbedingungen von Google.
              Der Anbieter übernimmt keine Garantie für die inhaltliche Richtigkeit oder Vollständigkeit der KI-generierten
              Antworten. Für durch fehlgeleitete KI-Buchungen entstandene Schäden haftet der Anbieter nur im Rahmen von § 9.
            </p>
          </AgbSection>

          <AgbSection para="§ 9" title="Haftung">
            <p>
              <strong>9.1</strong> Der Anbieter haftet unbeschränkt für Schäden aus der Verletzung des Lebens, des Körpers
              oder der Gesundheit, für vorsätzlich oder grob fahrlässig verursachte Schäden, bei arglistigem Verschweigen
              eines Mangels, im Umfang einer übernommenen Garantie sowie nach dem Produkthaftungsgesetz.
            </p>
            <p>
              <strong>9.2</strong> Für leicht fahrlässig verursachte Schäden haftet der Anbieter nur bei Verletzung einer
              wesentlichen Vertragspflicht. Wesentlich sind Pflichten, deren Erfüllung die ordnungsgemäße Durchführung des
              Vertrags überhaupt erst ermöglicht und auf deren Einhaltung der Nutzer regelmäßig vertrauen darf
              (Kardinalpflichten). In diesem Fall ist die Haftung auf den vertragstypischen, bei Vertragsschluss
              vorhersehbaren Schaden begrenzt.
            </p>
            <p>
              <strong>9.3</strong> Im Übrigen ist die Haftung für leicht fahrlässig verursachte Schäden ausgeschlossen.
            </p>
            <p>
              <strong>9.4</strong> Für den Verlust von Daten haftet der Anbieter im Rahmen von 9.2 nur in Höhe des Aufwands,
              der bei ordnungsgemäßer und regelmäßiger Datensicherung durch den Nutzer (insbesondere über den Datenexport
              im Admin-Dashboard) zur Wiederherstellung erforderlich gewesen wäre. 9.1 bleibt unberührt.
            </p>
            <p>
              <strong>9.5</strong> Die vorstehenden Regelungen gelten auch zugunsten der gesetzlichen Vertreter,
              Mitarbeiter und Erfüllungsgehilfen des Anbieters, einschließlich der eingesetzten Dienstleister
              (z. B. Google, Resend, Stripe). Für deren Verschulden haftet der Anbieter wie für eigenes.
            </p>
            <p>
              <strong>9.6</strong> Für Schäden, die allein darauf beruhen, dass der Nutzer eigene Pflichten verletzt
              (z. B. fehlerhafte Konfiguration der Plattform, fehlende oder fehlerhafte eigene Datenschutzerklärung
              gegenüber seinen Endkunden), haftet der Anbieter nicht.
            </p>
            <p>
              <strong>9.7</strong> Diese Haftungsregelung gilt auch für Ansprüche aus dem Auftragsverarbeitungsvertrag (AVV).
              Die Haftung gegenüber betroffenen Personen nach Art. 82 DSGVO bleibt unberührt.
            </p>
          </AgbSection>

          <AgbSection para="§ 10" title="Geistiges Eigentum">
            <p>
              <strong>10.1</strong> Der Anbieter räumt dem Nutzer für die Dauer des Vertrags ein nicht ausschließliches,
              nicht übertragbares Recht ein, die Plattform im Rahmen dieser AGB zu nutzen. Alle sonstigen Rechte an der
              Plattform, deren Code, Design und Marken verbleiben beim Anbieter.
            </p>
            <p>
              <strong>10.2</strong> Der Nutzer erhält einen <strong>API-Key</strong> zur Integration der Buchungsfunktionen
              in seine eigene Website. Dieser API-Key ist vertraulich zu behandeln, darf nicht weitergegeben werden und
              ist nur für die in diesen AGB beschriebenen Zwecke zu verwenden.
            </p>
            <p>
              <strong>10.3</strong> Daten, die der Nutzer in die Plattform eingibt (z. B. Kundendaten, Termineinträge,
              Ressourcen), verbleiben im Eigentum des Nutzers. Der Anbieter verarbeitet diese ausschließlich zur
              Vertragserfüllung gemäß dem AVV.
            </p>
          </AgbSection>

          <AgbSection para="§ 11" title="Änderungen der AGB und des Leistungsumfangs">
            <p>
              <strong>11.1</strong> Der Anbieter kann diese AGB und den Leistungsumfang anpassen, wenn ein sachlich
              gerechtfertigter Grund vorliegt (z. B. neue gesetzliche Anforderungen, Änderungen bei Drittanbietern,
              neue Funktionen). Änderungen, die die Hauptleistungspflichten oder den Preis betreffen oder das Verhältnis
              von Leistung und Gegenleistung zulasten des Nutzers verschieben, sind auf diesem Weg ausgeschlossen;
              für Preisänderungen gilt § 5.6.
            </p>
            <p>
              <strong>11.2</strong> Änderungen werden dem Nutzer per E-Mail mit einer Frist von mindestens
              <strong> 30 Tagen</strong> vor Inkrafttreten in Textform angekündigt. Der Nutzer kann den Änderungen bis
              zum Inkrafttreten widersprechen. Widerspricht er nicht, gelten die Änderungen ab Inkrafttreten als vereinbart.
            </p>
            <p>
              <strong>11.3</strong> Widerspricht der Nutzer, gelten die bisherigen Bedingungen fort. Beide Parteien können
              den Vertrag in diesem Fall zum Zeitpunkt des Inkrafttretens der Änderungen kündigen. Der Nutzer kann vorher
              seinen Datenexport anfordern.
            </p>
            <p>
              <strong>11.4</strong> Auf das Widerspruchsrecht, die Frist und die Folgen eines unterbliebenen Widerspruchs
              weist der Anbieter in der Ankündigungs-E-Mail ausdrücklich hin.
            </p>
          </AgbSection>

          <AgbSection para="§ 12" title="Datenschutz">
            <p>
              Informationen zur Verarbeitung personenbezogener Daten des Nutzers (z. B. im Rahmen der Registrierung,
              des Accountbetriebs und der Zahlungsabwicklung) finden sich in der{' '}
              <a href="/datenschutz" className="text-[#FF6D00] hover:underline">Datenschutzerklärung</a>.
              Die Verarbeitung von Endkundendaten im Auftrag des Nutzers ist im{' '}
              <a href="/avv" className="text-[#FF6D00] hover:underline">Auftragsverarbeitungsvertrag (AVV)</a> geregelt,
              der Bestandteil des Vertragsverhältnisses ist.
            </p>
          </AgbSection>

          <AgbSection para="§ 13" title="Schlussbestimmungen">
            <p>
              <strong>13.1 Anwendbares Recht:</strong> Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss
              des UN-Kaufrechts (CISG).
            </p>
            <p>
              <strong>13.2 Gerichtsstand:</strong> Für alle Streitigkeiten aus oder im Zusammenhang mit diesem Vertrag
              ist – soweit gesetzlich zulässig – <strong>Köln</strong> der ausschließliche Gerichtsstand
              (§ 38 Abs. 1 ZPO; gilt zwischen Kaufleuten und juristischen Personen des öffentlichen Rechts).
            </p>
            <p>
              <strong>13.3 Salvatorische Klausel:</strong> Sollte eine Bestimmung dieser AGB unwirksam sein oder werden,
              bleibt die Wirksamkeit der übrigen Bestimmungen unberührt. An die Stelle der unwirksamen Bestimmung tritt
              eine ihr inhaltlich möglichst nahekommende wirksame Regelung.
            </p>
            <p>
              <strong>13.4 Schriftformklausel:</strong> Mündliche Nebenabreden bestehen nicht. Änderungen dieser AGB
              bedürfen der Textform.
            </p>
            <p>
              <strong>13.5 Streitbeilegung:</strong> Der Anbieter ist nicht bereit und nicht verpflichtet, an
              Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen (da der Dienst ausschließlich
              an Unternehmer gerichtet ist).
            </p>
          </AgbSection>

          {/* Kontakt */}
          <section className="mt-10 p-5 bg-gray-50 rounded-xl border border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Fragen zu diesen AGB</p>
            <p className="text-sm text-gray-800 leading-relaxed">
              <strong>{LEGAL_OPERATOR_NAME}</strong><br />
              E-Mail: <a href="mailto:support@candlear.de" className="text-[#FF6D00] hover:underline">support@candlear.de</a><br />
              Telefon:{' '}
              <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-[#FF6D00] hover:underline">
                {SUPPORT_PHONE_DISPLAY}
              </a>
              <br />
              {LEGAL_ADDRESS_STREET}, {LEGAL_ADDRESS_CITY}
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}

function AgbSection({ para, title, children }: { para: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200 flex items-baseline gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <span className="text-[#FF6D00] font-mono text-sm">{para}</span>
        {title}
      </h2>
      <div className="text-sm text-gray-700 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}
